import { Component, Input, Output, EventEmitter, signal, inject, computed, HostListener } from '@angular/core';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../../core/auth/auth.service';

interface NavLeaf {
  kind: 'leaf';
  path: string;
  label: string;
  icon: string;
}

interface NavParent {
  kind: 'parent';
  key: string;
  label: string;
  icon: string;
  children: NavLeaf[];
}

type NavItem = NavLeaf | NavParent;

interface NavGroup {
  label: string;
  items: NavItem[];
}

const ADMIN_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Riset',
    items: [
      {
        kind: 'parent', key: 'dataset', label: 'Dataset & Split', icon: 'fa-solid fa-database',
        children: [
          { kind: 'leaf', path: '/research/dataset/overview',      label: 'Overview',        icon: 'fa-solid fa-chart-pie' },
          { kind: 'leaf', path: '/research/dataset/samples',       label: 'Samples',         icon: 'fa-solid fa-table' },
          { kind: 'leaf', path: '/research/dataset/label-mapping', label: 'Label Mapping',   icon: 'fa-solid fa-arrows-left-right' },
          { kind: 'leaf', path: '/research/dataset/exports',       label: 'Exports',         icon: 'fa-solid fa-file-export' },
          { kind: 'leaf', path: '/research/goemotions',            label: 'GoEmotions → ID', icon: 'fa-solid fa-language' },
        ],
      },
      {
        kind: 'parent', key: 'review', label: 'Review', icon: 'fa-solid fa-star-half-stroke',
        children: [
          { kind: 'leaf', path: '/dashboard',    label: 'Scraper Ulasan',   icon: 'fa-solid fa-gauge-high' },
          { kind: 'leaf', path: '/categories',   label: 'Kategori Wisata',  icon: 'fa-solid fa-tags' },
          { kind: 'leaf', path: '/destinations', label: 'Destinasi Wisata', icon: 'fa-solid fa-map-location-dot' },
          { kind: 'leaf', path: '/reviews',      label: 'Ulasan Destinasi', icon: 'fa-solid fa-comments' },
          { kind: 'leaf', path: '/emoticons',    label: 'Emoticons',        icon: 'fa-solid fa-icons' },
          { kind: 'leaf', path: '/language',     label: 'Language',         icon: 'fa-solid fa-language' },
        ],
      },
      { kind: 'leaf', path: '/researches', label: 'Penelitian', icon: 'fa-solid fa-microscope' },
      { kind: 'leaf', path: '/research/emotion-classes', label: 'Kelas Emosi', icon: 'fa-solid fa-tags' },
      { kind: 'leaf', path: '/research/lexicon', label: 'Lexicon Graph', icon: 'fa-solid fa-sitemap' },
      { kind: 'leaf', path: '/preprocessing', label: 'Preprocessing', icon: 'fa-solid fa-sliders' },
    ],
  },
  {
    label: 'Model',
    items: [
      {
        kind: 'parent', key: 'training', label: 'Training Eksperimen', icon: 'fa-solid fa-flask',
        children: [
          { kind: 'leaf', path: '/research/training',         label: 'Semua Eksperimen', icon: 'fa-solid fa-list' },
          { kind: 'leaf', path: '/research/training/new',     label: 'Buat Eksperimen',  icon: 'fa-solid fa-plus' },
          { kind: 'leaf', path: '/research/training/presets', label: 'Preset',            icon: 'fa-solid fa-bookmark' },
        ],
      },
    ],
  },
  {
    label: 'Evaluasi',
    items: [
      { kind: 'leaf', path: '/research/comparison', label: 'Perbandingan Model', icon: 'fa-solid fa-scale-balanced' },
      { kind: 'leaf', path: '/research/inference',  label: 'Inferensi Live',     icon: 'fa-solid fa-bullseye' },
    ],
  },
  {
    label: 'Sistem',
    items: [
      { kind: 'leaf', path: '/settings',     label: 'Pengaturan',       icon: 'fa-solid fa-sliders' },
      { kind: 'leaf', path: '/settings/llm', label: 'Konfigurasi LLM',  icon: 'fa-solid fa-robot' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { kind: 'leaf', path: '/admin/users', label: 'Manajemen Pengguna', icon: 'fa-solid fa-users-gear' },
      { kind: 'leaf', path: '/admin/sessions', label: 'Audit Sesi Login', icon: 'fa-solid fa-shield-halved' },
      { kind: 'leaf', path: '/annotators', label: 'Annotators', icon: 'fa-solid fa-users' },
      { kind: 'leaf', path: '/admin/backups', label: 'Backup Management', icon: 'fa-solid fa-database' },
      { kind: 'leaf', path: '/admin/backup-config', label: 'Backup Config', icon: 'fa-solid fa-cog' },

    ],
  },
];

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, CommonModule],
  styles: [`
    .sidebar { transition: width 0.25s cubic-bezier(0.4,0,0.2,1); }
    .nav-label, .brand-text, .collapse-text {
      transition: opacity 0.12s ease, max-width 0.25s ease;
      max-width: 180px; white-space: nowrap; overflow: hidden;
    }
    .sidebar.collapsed .nav-label,
    .sidebar.collapsed .brand-text,
    .sidebar.collapsed .collapse-text { opacity: 0; max-width: 0; }
    .children-enter { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 0.2s ease; }
    .children-enter.open { grid-template-rows: 1fr; }
    .children-enter > .children-inner { overflow: hidden; }
  `],
  template: `
    <aside
      class="sidebar flex flex-col border-r border-[var(--color-surface-border)]"
      [class.collapsed]="collapsed"
      (mouseenter)="onMouseEnter()"
      (mouseleave)="onMouseLeave()"
      style="width:100%; height:100%; background: linear-gradient(180deg, #0d1117 0%, #030712 100%)">

      <!-- Brand -->
      <div class="flex items-center h-[60px] px-3 border-b border-[var(--color-surface-border)] overflow-hidden shrink-0">
        <div class="flex items-center gap-3">
          <img src="android-chrome-512x512.png" 
               alt="SAD Logo" 
               class="w-8 h-8 rounded-lg shrink-0">
          <div class="brand-text flex flex-col">
            <span class="text-sm font-bold text-white leading-tight">SAD Sentiment</span>
            <span class="text-[10px] leading-tight" style="color: #06b6d4">Six Affective Dimensions</span>
          </div>
        </div>
      </div>

      <!-- Nav -->
      <nav class="flex-1 p-2 overflow-y-auto overflow-x-hidden min-h-0">
        @if (isGuest()) {
          <p class="nav-label text-[9px] font-semibold px-2 pt-3 pb-1.5 uppercase tracking-widest" style="color:#4b5563">Akses Tamu</p>
          @for (item of guestNav; track item.path) {
            <a [routerLink]="item.path" routerLinkActive="active-nav"
               [title]="collapsed ? item.label : ''"
               class="nav-link flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-gray-400 hover:text-gray-100 transition-all text-sm font-medium border border-transparent">
              <i [class]="item.icon + ' fa-nav shrink-0'"></i>
              <span class="nav-label">{{ item.label }}</span>
            </a>
          }
        } @else if (isAnnotator()) {
          <p class="nav-label text-[9px] font-semibold px-2 pt-3 pb-1.5 uppercase tracking-widest" style="color:#4b5563">Workspace</p>
          <a [routerLink]="workspacePath()" routerLinkActive="active-nav"
             class="nav-link flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-gray-400 hover:text-gray-100 transition-all text-sm font-medium border border-transparent">
            <i class="fa-solid fa-pen-to-square fa-nav shrink-0"></i>
            <span class="nav-label">My Workspace</span>
          </a>
          @for (item of annotatorNav; track item.path) {
            <a [routerLink]="item.path" routerLinkActive="active-nav"
               [title]="collapsed ? item.label : ''"
               class="nav-link flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-gray-400 hover:text-gray-100 transition-all text-sm font-medium border border-transparent">
              <i [class]="item.icon + ' fa-nav shrink-0'"></i>
              <span class="nav-label">{{ item.label }}</span>
            </a>
          }
        } @else {
          @for (group of groups(); track group.label) {
            @if (collapsed) {
              <hr class="border-t border-[var(--color-surface-border)] mx-2 my-2">
            } @else {
              <p class="nav-label text-[9px] font-semibold px-2 pt-3 pb-1.5 uppercase tracking-widest"
                 style="color:#4b5563">{{ group.label }}</p>
            }

            @for (item of group.items; track item.label) {
              @if (item.kind === 'leaf') {
                <a
                  [routerLink]="item.path"
                  routerLinkActive="active-nav"
                  [routerLinkActiveOptions]="{ exact: true }"
                  [title]="collapsed ? item.label : ''"
                  class="nav-link flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-gray-400 hover:text-gray-100 transition-all text-sm font-medium border border-transparent">
                  <i [class]="item.icon + ' fa-nav shrink-0'"></i>
                  <span class="nav-label">{{ item.label }}</span>
                </a>
              } @else {
                <button
                  (click)="toggleParent(item.key)"
                  [title]="collapsed ? item.label : ''"
                  class="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-all text-sm font-semibold border border-transparent"
                  [class]="isParentActive(item) ? 'text-gray-100' : 'text-gray-400 hover:text-gray-200'">
                  <i [class]="item.icon + ' fa-nav shrink-0'"></i>
                  <span class="nav-label flex-1 text-left">{{ item.label }}</span>
                  <i class="nav-label fa-solid fa-chevron-down fa-xs transition-transform duration-200 shrink-0"
                     [class.rotate-180]="isExpanded(item.key)"></i>
                </button>
                <div class="children-enter" [class.open]="isExpanded(item.key)">
                  <div class="children-inner">
                    @for (child of item.children; track child.path) {
                      <a
                        [routerLink]="child.path"
                        routerLinkActive="active-nav"
                        [routerLinkActiveOptions]="{ exact: true }"
                        [title]="collapsed ? child.label : ''"
                        class="nav-link flex items-center gap-3 pl-7 pr-2.5 py-2 rounded-xl text-gray-400 hover:text-gray-100 transition-all text-sm border border-transparent">
                        <i [class]="child.icon + ' fa-nav shrink-0 text-xs'"></i>
                        <span class="nav-label">{{ child.label }}</span>
                      </a>
                    }
                  </div>
                </div>
              }
            }
          }
        }
      </nav>

      <!-- Collapse toggle -->
      <div class="p-2 border-t border-[var(--color-surface-border)] shrink-0">
        <button
          (click)="toggleCollapsed.emit()"
          [title]="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          class="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-gray-500 hover:text-gray-200 hover:bg-[var(--color-surface-hover)] transition-all">
          <i class="fa-solid fa-angles-left fa-nav shrink-0 transition-transform duration-200"
             [class.rotate-180]="collapsed"></i>
          <span class="collapse-text text-xs">Collapse</span>
        </button>
      </div>
    </aside>
  `,
})
export class SidebarComponent {
  @Input() collapsed = false;
  @Output() toggleCollapsed = new EventEmitter<void>();
  @Output() hoveredChange = new EventEmitter<boolean>();

  private router = inject(Router);
  private auth = inject(AuthService);
  private isDesktop = signal(window.innerWidth >= 768);

  constructor() {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(() => this.expandedKeys.set(new Set()));
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isDesktop.set(window.innerWidth >= 768);
  }

  onMouseEnter(): void {
    if (this.isDesktop()) this.hoveredChange.emit(true);
  }
  onMouseLeave(): void {
    if (this.isDesktop()) this.hoveredChange.emit(false);
  }

  groups = computed(() => {
    const role = this.auth.currentUser()?.role;
    if (role === 'superadmin' || role === 'admin') return ADMIN_NAV_GROUPS;
    if (role === 'researcher') {
      // Researcher: everything except the scraper/system group; the scraper
      // dashboard link (/dashboard) is removed from the Review parent. They
      // keep a scoped user-management entry (annotator + guest only).
      const groups = ADMIN_NAV_GROUPS
        .filter(g => g.label !== 'Admin' && g.label !== 'Sistem')
        .map(g => ({
          ...g,
          items: g.items.map(item =>
            item.kind === 'parent'
              ? { ...item, children: item.children.filter(c => c.path !== '/dashboard') }
              : item),
        }));
      groups.push({
        label: 'Pengguna',
        items: [
          { kind: 'leaf', path: '/admin/users', label: 'Manajemen Pengguna', icon: 'fa-solid fa-users-gear' },
      { kind: 'leaf', path: '/admin/sessions', label: 'Audit Sesi Login', icon: 'fa-solid fa-shield-halved' },
          { kind: 'leaf', path: '/annotators', label: 'Annotators', icon: 'fa-solid fa-users' },
        ],
      });
      return groups;
    }
    return [];
  });

  isGuest = computed(() => this.auth.currentUser()?.role === 'guest');
  readonly guestNav = [
    { path: '/research/dataset/overview', label: 'Dataset', icon: 'fa-solid fa-database' },
    { path: '/research/inference',        label: 'Inferensi Live', icon: 'fa-solid fa-bullseye' },
  ];
  readonly annotatorNav = [
    { path: '/annotator/tasks',      label: 'Tugas Saya',       icon: 'fa-solid fa-list-check' },
    { path: '/annotator/guidelines', label: 'Panduan Anotasi',  icon: 'fa-solid fa-book-open' },
  ];
  private expandedKeys = signal<Set<string>>(new Set());

  isAnnotator = computed(() => this.auth.currentUser()?.role === 'annotator');
  workspacePath = computed(() => {
    const id = this.auth.currentUser()?.annotator_id;
    return id ? `/annotators/${id}` : '/dashboard';
  });

  isExpanded(key: string): boolean {
    return this.expandedKeys().has(key);
  }

  toggleParent(key: string): void {
    this.expandedKeys.update(s => {
      if (s.has(key)) {
        const next = new Set(s);
        next.delete(key);
        return next;
      }
      return new Set([key]);
    });
  }

  isParentActive(item: NavParent): boolean {
    return item.children.some(c => this.router.isActive(c.path, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    }));
  }
}
